import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import * as rx from "rxjs";

import type {
  QuerySuggestionResult,
  QuerySuggestionsResult,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import type { Service } from "./service";
import type { CachedRequestOptions } from "./service/cache/query-suggestions-cache";
import type { QuerySuggestionsOptions } from "./types";

const querySuggestionsService = services.get<Service>(
  ServiceId.QUERY_SUGGESTIONS,
);

export default querySuggestionsService;

/**
 * Fetch batch of query suggestions without looking at cache.
 * @param options The request options for query suggestions
 * @returns A promise that resolves to the query suggestions result
 */
export function genQuerySuggestions(
  options: QuerySuggestionsOptions,
): Promise<QuerySuggestionsResult> {
  return rx.lastValueFrom(querySuggestionsService.genQuerySuggestions(options));
}

/**
 * Get cached query suggestions or fetch new ones.
 * To cache fetched results pass { shouldHydrateCache: true }
 * To define a requirement for the minimum size of the cache { cacheCapacityUsefulnessThreshold: 5, etc }
 * @param options The request options for query suggestions
 * @returns A promise that resolves to the query suggestions result
 */
export function getCachedQuerySuggestionsOrFetch(
  options: QuerySuggestionsOptions & CachedRequestOptions,
): Promise<QuerySuggestionResult[]> {
  return rx.lastValueFrom(
    querySuggestionsService.getCachedQuerySuggestionsOrFetch(options),
  );
}

/**
 * Manually set query suggestions to a specific cache scope.
 * @param options The request options for query suggestions
 * @param suggestions The query suggestions to set
 */
export function setQuerySuggestionsToCacheScope(
  options: QuerySuggestionsOptions,
  suggestions: QuerySuggestionResult[],
) {
  querySuggestionsService.setQuerySuggestionsToCacheScope(options, suggestions);
}

/**
 * Manually invalidate a specific cache scope.
 * @param options The request options for query suggestions
 */
export function invalidateCacheScope(options: QuerySuggestionsOptions) {
  querySuggestionsService.invalidateCacheScope(options);
}

/**
 * Start the query suggestions sync job.
 */
export function startSyncQuerySuggestions() {
  return rx.firstValueFrom(querySuggestionsService.startSyncQuerySuggestions());
}

/**
 * Cancel the query suggestions sync job.
 */
export function cancelSyncQuerySuggestions() {
  return rx.firstValueFrom(
    querySuggestionsService.cancelSyncQuerySuggestions(),
  );
}
